import { FC } from "react";
import {
  DetailsListLayoutMode,
  IDetailsRowProps,
  IShimmerColors,
  IShimmerElement,
  SelectionMode,
  Shimmer,
  ShimmerElementsGroup,
  ShimmeredDetailsList,
} from "@fluentui/react";
import { useTheme } from "cayo.ui";
import { ShimmerBar } from "@cayo/core/components/Schemes/SchemeSkeleton/components/ShimmerBar";
import { schemeSkeletonConstants } from "@cayo/core/components/Schemes/SchemeSkeleton/constants";
import {
  CommandBarRow,
  Container,
  FilterBarRow,
  GrayStrip,
  Grid,
  HeaderShimmerRow,
} from "@cayo/core/components/Schemes/SchemeSkeleton/SchemeSkeleton.styles";
import { schemeSkeletonUtils } from "@cayo/core/components/Schemes/SchemeSkeleton/utils";

const {
  HEADER_BAR_HEIGHT,
  BODY_BAR_HEIGHT,
  CHECKBOX_COLUMN_WIDTH,
  DATA_COLUMN_WIDTHS,
  HEADER_BAR_WIDTHS,
  COMMAND_BAR_BUTTON_WIDTHS,
  FILTER_BAR_ITEM_WIDTHS,
  GRID_COLUMNS,
} = schemeSkeletonConstants;

const { bodyBarWidth, buildShimmerRow } = schemeSkeletonUtils;

const headerShimmerElements: IShimmerElement[] = buildShimmerRow(
  [HEADER_BAR_HEIGHT, ...HEADER_BAR_WIDTHS],
  [CHECKBOX_COLUMN_WIDTH, ...DATA_COLUMN_WIDTHS],
  HEADER_BAR_HEIGHT
);

const handleRenderDetailsHeader = () => {
  return (
    <HeaderShimmerRow>
      <Shimmer
        customElementsGroup={<ShimmerElementsGroup shimmerElements={headerShimmerElements} />}
      />
    </HeaderShimmerRow>
  );
};

const handleRenderCustomPlaceholder = (_rowProps?: IDetailsRowProps, index?: number) => {
  const rowIndex = index ?? 0;
  const elements = buildShimmerRow(
    [BODY_BAR_HEIGHT, ...DATA_COLUMN_WIDTHS.map((_, c) => bodyBarWidth(rowIndex, c))],
    [CHECKBOX_COLUMN_WIDTH, ...DATA_COLUMN_WIDTHS],
    BODY_BAR_HEIGHT
  );
  return <Shimmer customElementsGroup={<ShimmerElementsGroup shimmerElements={elements} />} />;
};

interface SchemeSkeletonProps {
  isDataLoaded?: boolean;
}

const SchemeSkeleton: FC<SchemeSkeletonProps> = ({ isDataLoaded = false }) => {
  const { palette } = useTheme().officeTheme;

  const filterBarShimmerColors: IShimmerColors = {
    shimmer: palette.neutralLighterAlt,
    shimmerWave: palette.neutralLight,
  };

  return (
    <Container aria-label="Loading" aria-busy={!isDataLoaded}>
      <CommandBarRow>
        {COMMAND_BAR_BUTTON_WIDTHS.map((w, i) => (
          <ShimmerBar key={i} width={w} height={20} isDataLoaded={isDataLoaded} />
        ))}
      </CommandBarRow>

      <GrayStrip>
        <FilterBarRow>
          {FILTER_BAR_ITEM_WIDTHS.map((width, i) => (
            <ShimmerBar
              key={i}
              width={width}
              height={20}
              shimmerColors={filterBarShimmerColors}
              isDataLoaded={isDataLoaded}
            />
          ))}
        </FilterBarRow>
      </GrayStrip>

      <Grid>
        <ShimmeredDetailsList
          items={[]}
          columns={GRID_COLUMNS}
          enableShimmer={!isDataLoaded}
          shimmerLines={24}
          selectionMode={SelectionMode.none}
          layoutMode={DetailsListLayoutMode.fixedColumns}
          onRenderDetailsHeader={handleRenderDetailsHeader}
          onRenderCustomPlaceholder={handleRenderCustomPlaceholder}
        />
      </Grid>
    </Container>
  );
};

export default SchemeSkeleton;
